import { useState, useMemo } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { trpc } from "@/lib/trpc";
import { safeFormat } from "@/lib/dateUtils";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link, useSearch } from "wouter";
import { ArrowLeft, Calendar, Clock, User, Scissors, Plus, Loader2, Star, CheckCircle2, XCircle } from "lucide-react";
import { useEffect } from "react";
import { toast } from "sonner";
import { startLogin } from "@/const";
import { BeautyChatBot } from "@/components/BeautyChatBot";
import { ReviewModal } from "@/components/ReviewModal";
import { StarRating } from "@/components/StarRating";

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  confirmed: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
  completed: "bg-blue-100 text-blue-800 border-blue-200",
  no_show: "bg-gray-100 text-gray-800 border-gray-200",
};

const STATUS_LABELS: Record<string, string> = {
  pending: "Pendente",
  confirmed: "Confirmado",
  cancelled: "Cancelado",
  completed: "Concluído",
  no_show: "Não compareceu",
};

export default function MyAppointments() {
  const { user, isAuthenticated } = useAuth();
  const utils = trpc.useUtils();
  const search = useSearch();
  const params = new URLSearchParams(search);
  const paymentStatus = params.get("payment");

  const [reviewAppt, setReviewAppt] = useState<{
    id: number;
    professionalId: number;
    serviceId: number;
    serviceName: string;
    professionalName: string;
  } | null>(null);

  useEffect(() => {
    if (paymentStatus === "success") {
      toast.success("Pagamento realizado com sucesso!", { icon: <CheckCircle2 className="text-green-500" /> });
    } else if (paymentStatus === "cancelled") {
      toast.info("Pagamento cancelado.", { icon: <XCircle className="text-gray-400" /> });
    }
  }, [paymentStatus]);

  const { data: appointments, isLoading } = trpc.appointments.getMyAppointments.useQuery(
    undefined,
    { enabled: isAuthenticated }
  );

  // Determinar salonId dos agendamentos (usa o primeiro disponível)
  const salonId = useMemo(() => {
    return appointments?.find((a) => a.salonId)?.salonId ?? 1;
  }, [appointments]);

  // Buscar reviews UMA VEZ no componente pai — evita useQuery dentro do .map()
  const reviewsInput = useMemo(() => ({ salonId, limit: 200 }), [salonId]);
  const { data: allReviews = [] } = trpc.reviews.listPublic.useQuery(
    reviewsInput,
    { enabled: isAuthenticated && (appointments?.some((a) => a.status === "completed") ?? false) }
  );

  // For MyAppointments we don't need salonId for these queries since they're client-side
  const { data: professionals } = trpc.professionals.list.useQuery({ salonId: 0 }, { enabled: false });
  const { data: services } = trpc.services.list.useQuery({ salonId: 0 }, { enabled: false });

  const cancelMutation = trpc.appointments.cancel.useMutation({
    onSuccess: () => {
      toast.success("Agendamento cancelado.");
      utils.appointments.getMyAppointments.invalidate();
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  // Pagamento via Stripe
  const checkoutMutation = trpc.salon.createSubscriptionCheckout.useMutation({
    onSuccess: (data: { url: string | null }) => {
      if (data.url) {
        toast.info("Redirecionando para o pagamento...");
        window.open(data.url, "_blank");
      }
    },
    onError: (e: { message: string }) => toast.error(e.message),
  });

  const getProfName = (id: number) => professionals?.find((p) => p.id === id)?.name ?? "—";
  const getServiceName = (id: number) => services?.find((s) => s.id === id)?.name ?? "—";

  // Track reviewed appointments locally
  const [reviewedIds, setReviewedIds] = useState<Set<number>>(new Set());

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-muted-foreground mb-4">Faça login para ver seus agendamentos</p>
          <Button className="bg-primary text-primary-foreground" onClick={() => startLogin("/my-appointments")}>Entrar</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-border/50 shadow-sm">
        <div className="container flex items-center justify-between h-16">
          <Link href="/">
            <div className="flex items-center gap-2 cursor-pointer">
              <ArrowLeft className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm text-muted-foreground">Voltar</span>
            </div>
          </Link>
          <h1 className="font-[Cormorant_Garamond,serif] text-xl font-semibold">Meus Agendamentos</h1>
          <Link href="/booking">
            <Button size="sm" className="bg-primary text-primary-foreground">
              <Plus className="w-4 h-4 mr-1" /> Novo
            </Button>
          </Link>
        </div>
      </nav>

      <div className="container pt-24 pb-16 max-w-2xl">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : !appointments || appointments.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-primary" />
            </div>
            <h3 className="text-lg font-semibold text-foreground mb-2">Nenhum agendamento</h3>
            <p className="text-muted-foreground mb-6">Você ainda não tem agendamentos. Que tal marcar um?</p>
            <Link href="/booking">
              <Button className="bg-primary text-primary-foreground">Agendar Agora</Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {appointments.map((appt) => {
              // Buscar review deste agendamento nos dados já carregados — sem useQuery por card
              const review = allReviews.find((r: { appointmentId: number }) => r.appointmentId === appt.id);
              return (
                <AppointmentCard
                  key={appt.id}
                  appt={appt}
                  profName={getProfName(appt.professionalId)}
                  serviceName={getServiceName(appt.serviceId)}
                  review={review}
                  onCancel={() => cancelMutation.mutate({ id: appt.id })}
                  isCancelling={cancelMutation.isPending}
                  onPay={() => toast.info("Para pagar, entre em contato com o salão.", { description: "Pagamento online disponível em breve." })}
                  isPaying={checkoutMutation.isPending}
                  onReview={() =>
                    setReviewAppt({
                      id: appt.id,
                      professionalId: appt.professionalId,
                      serviceId: appt.serviceId,
                      serviceName: getServiceName(appt.serviceId),
                      professionalName: getProfName(appt.professionalId),
                    })
                  }
                />
              );
            })}
          </div>
        )}
      </div>

      {reviewAppt && (
        <ReviewModal
          open={!!reviewAppt}
          onClose={() => setReviewAppt(null)}
          appointmentId={reviewAppt.id}
          professionalId={reviewAppt.professionalId}
          serviceId={reviewAppt.serviceId}
          serviceName={reviewAppt.serviceName}
          professionalName={reviewAppt.professionalName}
        />
      )}

      <BeautyChatBot userId={user?.id} />
    </div>
  );
}

// ─── Appointment Card ──────────────────────────────────────────────────────────
// NOTA: Sem hooks aqui — useQuery foi movido para o componente pai para evitar
// o React error #185 (Too many re-renders) causado por objetos literais inline
// dentro de .map() que criam novas referências a cada render.
function AppointmentCard({
  appt,
  profName,
  serviceName,
  review,
  onCancel,
  isCancelling,
  onPay,
  isPaying,
  onReview,
}: {
  appt: any;
  profName: string;
  serviceName: string;
  review?: { rating: number; comment?: string | null; appointmentId: number } | undefined;
  onCancel: () => void;
  isCancelling: boolean;
  onPay: () => void;
  isPaying: boolean;
  onReview: () => void;
}) {
  return (
    <Card className="border-border/50 hover:border-primary/30 transition-all duration-200 hover:shadow-md">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 mt-0.5">
              <Scissors className="w-5 h-5 text-primary" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-foreground truncate">{serviceName}</h3>
              <div className="flex flex-wrap items-center gap-3 mt-1.5">
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <User className="w-3 h-3 shrink-0" />
                  {profName}
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Calendar className="w-3 h-3 shrink-0" />
                  {safeFormat(appt.startTime, "dd/MM/yyyy")}
                </div>
                <div className="flex items-center gap-1 text-xs text-muted-foreground">
                  <Clock className="w-3 h-3 shrink-0" />
                  {safeFormat(appt.startTime, "HH:mm")}–{safeFormat(appt.endTime, "HH:mm")}
                </div>
              </div>
              {appt.notes && (
                <p className="text-xs text-muted-foreground mt-1.5 italic">"{appt.notes}"</p>
              )}
              {/* Show existing review */}
              {review && (
                <div className="mt-2 flex items-center gap-2">
                  <StarRating rating={review.rating} size="sm" />
                  {review.comment && (
                    <span className="text-xs text-muted-foreground italic truncate max-w-[160px]">
                      "{review.comment}"
                    </span>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col items-end gap-2 shrink-0">
            <Badge className={STATUS_COLORS[appt.status]}>
              {STATUS_LABELS[appt.status]}
            </Badge>
            <span className="text-sm font-semibold text-primary">
              R$ {Number(appt.totalPrice ?? 0).toFixed(2).replace(".", ",")}
            </span>

            {/* Action buttons */}
            {["pending", "confirmed"].includes(appt.status) && (
              <>
                {appt.paymentStatus === "unpaid" && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs border-primary/40 text-primary hover:bg-primary/5"
                    onClick={onPay}
                    disabled={isPaying}
                  >
                    {isPaying ? <Loader2 className="h-3 w-3 animate-spin" /> : "Pagar"}
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="text-xs text-destructive border-destructive/30 hover:bg-destructive/5"
                  onClick={onCancel}
                  disabled={isCancelling}
                >
                  Cancelar
                </Button>
              </>
            )}

            {/* Review button for completed appointments without review */}
            {appt.status === "completed" && !review && (
              <Button
                variant="outline"
                size="sm"
                className="text-xs border-amber-400/50 text-amber-700 hover:bg-amber-50 gap-1"
                onClick={onReview}
              >
                <Star className="h-3 w-3" />
                Avaliar
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
